import { clone } from "@repo/helpers";
import * as baseContext from "@repo/helpers/src/contexts/editableItemContext";
import { EditableItemProviderProps, getEditableItemContext } from "@repo/helpers/src/contexts/editableItemContext";
import { MvcLocation, RepFieldAudit } from "@repo/models";
import { Context, useContext } from "react";

import { AssociationFilters, AssociationsInfo, LocationAssociations, LocationStatus } from "../models";
import { itemType } from "./pendingChangesContext";

export const LocationContext = getEditableItemContext<MvcLocation>();
export const StatusHistoryContext = getEditableItemContext<LocationStatus>();
export const AssociationsInfoContext = getEditableItemContext<AssociationsInfo>();
export const RepFieldAuditContext = getEditableItemContext<RepFieldAudit>();

const emptyLocation = {} as MvcLocation;

export function useLocationContext() {
	const context = useContext(LocationContext);
	const location = context.item ?? emptyLocation;
	const savedLocation = context.savedItem ?? emptyLocation;
	const locationId = location.locationId;

	return {
		...context,
		location,
		savedLocation,
		locationId,
		locationLoaded: context.itemLoaded,
		setLocationLoaded: context.setItemLoaded,
	};
}

export function useStatusHistoryContext() {
	return useContext(StatusHistoryContext);
}

export function useRepFieldAuditContext() {
	return useContext(RepFieldAuditContext);
}

export function useAssociationsInfoContext() {
	const context = useContext(AssociationsInfoContext);
	const { item: associationsInfo, updateItem, setHasPendingChanges } = context;
	const { associations = null, originalAssociations = null, saving = false, filters = {} } = associationsInfo ?? {};

	// Plain functions: every marketer and branch row calls this hook, and the handlers are only invoked on click
	const setAssociations = (newAssociations: LocationAssociations[]) => {
		updateItem({ associations: newAssociations });
		setHasPendingChanges(JSON.stringify(newAssociations) != JSON.stringify(originalAssociations));
	};

	const resetAssociations = () => setAssociations(originalAssociations);

	const updateMarketer = (marketerId: number, marketerEnabled: boolean, limitMarketerAssociations: boolean) => {
		const originalMarketer = originalAssociations.find((a) => a.marketerId == marketerId);
		const newAssociations = clone(associations);
		const marketer = newAssociations.find((a) => a.marketerId == marketerId);
		marketer.marketerEnabled = marketerEnabled;
		marketer.branches.forEach((b) => {
			const { branchEnabled: originalBranchEnabled } = originalMarketer.branches.find(
				(b2) => b2.branchId == b.branchId,
			);
			b.branchEnabled = limitMarketerAssociations || marketerEnabled ? false : originalBranchEnabled;
		});
		setAssociations(newAssociations);
	};

	const updateBranch = (marketerId: number, branchId: number, branchEnabled: boolean) => {
		const newAssociations = clone(associations);
		const branch = newAssociations
			.find((a) => a.marketerId == marketerId)
			.branches.find((b) => b.branchId == branchId);
		branch.branchEnabled = branchEnabled;
		setAssociations(newAssociations);
	};

	const setFilters = (filters: AssociationFilters) => updateItem({ filters });

	const setSaving = (saving: boolean) => updateItem({ saving });

	return {
		...context,
		associations,
		originalAssociations,
		filters,
		saving,
		updateMarketer,
		updateBranch,
		resetAssociations,
		setFilters,
		setSaving,
	};
}

export const LocationProvider = getProvider(LocationContext, "location");
export const StatusHistoryProvider = getProvider(StatusHistoryContext, "statusHistory");
export const AssociationsInfoProvider = getProvider(AssociationsInfoContext, "associationsInfo");
export const RepFieldAuditProvider = getProvider(RepFieldAuditContext, "repFieldAudit");

function getProvider<T>(context: Context<baseContext.EditableItemContextType<T>>, type: itemType) {
	return function (props: EditableItemProviderProps<T>) {
		return baseContext.EditableItemProvider(context, props, type);
	};
}
